import { Car, HelpCircle, Plus, X } from 'lucide-react';
import {
  FieldArrayWithId,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
} from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import { CoverageFormData } from '../types';

interface VehiclesSectionProps {
  register: UseFormRegister<CoverageFormData>;
  vehicleFields: FieldArrayWithId<CoverageFormData, 'vehicles', 'id'>[];
  appendVehicle: UseFieldArrayAppend<CoverageFormData, 'vehicles'>;
  removeVehicle: UseFieldArrayRemove;
}

const FieldWithTooltip = ({
  label,
  tooltip,
  children,
}: {
  label: string;
  tooltip: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <Label className="text-sm font-medium">{label}</Label>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <p className="text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    {children}
  </div>
);

export const VehiclesSection = ({
  register,
  vehicleFields,
  appendVehicle,
  removeVehicle,
}: VehiclesSectionProps) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              <Car className="h-5 w-5 text-gray-600" />
              Vehicles
              <span className="text-xs font-normal bg-gray-100 text-gray-600 px-2 py-1 rounded">
                Optional
              </span>
            </CardTitle>
            <p className="text-sm text-gray-600">
              Add vehicles covered by this policy
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              appendVehicle({
                make: null,
                model: null,
                year: null,
                vin: null,
                license_plate: null,
                license_plate_state: null,
                color: null,
              })
            }
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Vehicle
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {vehicleFields.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
            <Car className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No vehicles added
            </h3>
            <p className="text-gray-600 mb-4">
              Add vehicles that are covered under this insurance policy
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendVehicle({
                  make: null,
                  model: null,
                  year: null,
                  vin: null,
                  license_plate: null,
                  license_plate_state: null,
                  color: null,
                })
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Add First Vehicle
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {vehicleFields.map((field, index) => (
              <Card key={field.id} className="border-l-4 border-l-green-500">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Car className="h-5 w-5 text-green-600" />
                      <h4 className="text-lg font-semibold">
                        Vehicle {index + 1}
                      </h4>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeVehicle(index)}
                      className="text-gray-500 hover:text-red-600"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Basic Vehicle Information */}
                  <div className="space-y-4">
                    <h5 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
                      Basic Information
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FieldWithTooltip
                        label="Make"
                        tooltip="The manufacturer of the vehicle (e.g., Toyota, Ford, Honda)"
                      >
                        <Input
                          placeholder="e.g., Toyota"
                          {...register(`vehicles.${index}.make`)}
                        />
                      </FieldWithTooltip>

                      <FieldWithTooltip
                        label="Model"
                        tooltip="The model name of the vehicle (e.g., Camry, F-150, Accord)"
                      >
                        <Input
                          placeholder="e.g., Camry"
                          {...register(`vehicles.${index}.model`)}
                        />
                      </FieldWithTooltip>

                      <FieldWithTooltip
                        label="Year"
                        tooltip="The model year of the vehicle"
                      >
                        <Input
                          type="number"
                          placeholder="e.g., 2020"
                          min="1900"
                          max={new Date().getFullYear() + 1}
                          {...register(`vehicles.${index}.year`, {
                            valueAsNumber: true,
                          })}
                        />
                      </FieldWithTooltip>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FieldWithTooltip
                        label="Color"
                        tooltip="The primary color of the vehicle"
                      >
                        <Input
                          placeholder="e.g., Silver, Black, White"
                          {...register(`vehicles.${index}.color`)}
                        />
                      </FieldWithTooltip>

                      <FieldWithTooltip
                        label="License Plate"
                        tooltip="The license plate number of the vehicle"
                      >
                        <Input
                          placeholder="e.g., ABC-1234"
                          {...register(`vehicles.${index}.license_plate`)}
                          className="uppercase"
                          style={{ textTransform: 'uppercase' }}
                        />
                      </FieldWithTooltip>
                    </div>
                  </div>

                  {/* Vehicle Identification */}
                  <div className="space-y-4">
                    <h5 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
                      Vehicle Identification
                    </h5>
                    <FieldWithTooltip
                      label="VIN (Vehicle Identification Number)"
                      tooltip="The 17-character unique identifier for this vehicle. Usually found on the dashboard or driver's side door frame."
                    >
                      <Input
                        placeholder="e.g., 1HGBH41JXMN109186 (17 characters)"
                        {...register(`vehicles.${index}.vin`)}
                        maxLength={17}
                        className="font-mono uppercase"
                        style={{ textTransform: 'uppercase' }}
                      />
                    </FieldWithTooltip>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Add Another Vehicle Button */}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendVehicle({
                  make: null,
                  model: null,
                  year: null,
                  vin: null,
                  license_plate: null,
                  license_plate_state: null,
                  color: null,
                })
              }
              className="w-full border-dashed border-2 py-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Another Vehicle
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
